/* eslint-disable max-lines */
// deps
import React, { useCallback, useEffect, useState, memo } from "react";
import { NavigationTypes } from "@swiggy-private/native-bridge";

// external components
import {
    LottiePlayer,
    LoginPage,
    NonAppUser,
    Ineligible,
    CrossIcon,
} from "@swiggy-private/gamification-frontend-ui/dist/LoyaltyScanDeposit";
// api
import { availDineCash, getLoyaltyScanData } from "api/loyaltyScan";

// internal components
import ErrorWrapper from "components/ErrorWrapper";
import FullPageLoader from "components/FullPageLoader";
import PageWrapper from "components/PageWrapper";

// context
import { useSwiggyHeaders } from "context/SwiggyHeadersContext";

// hooks
import { useDineoutAnalytics } from "hooks/useDineoutAnalytics";
import { useFetchUserLoggedIn } from "hooks/useFetchUserLoggedIn";
// eslint-disable-next-line import/order
import { useRouter } from "next/router";
// eslint-disable-next-line import/order
import { LOYALTY_STATUS } from "constants/loyalty-scan";

// styles
import { Container } from "styles/loyalty-scan";

// utils
import { closeWebview, getBridgeSdk } from "utils/bridgeSdk";
import { checkIsEligible, DINEOUT_HOME_DEEPLINK, TRY_AGAIN } from "utils/loyaltyHelper";
import { handleLogin as handleLoginFn } from "utils/userLoggedin";

interface IIneligiblePayload {
    text: string;
    subText: string;
    bannerMessage: {
        text: string;
        subText: string;
    };
}

interface IEligiblePayload {
    text: string;
    subText: string;
    cashPillAmount: string;
    rewardsExpiryDays: string;
}

const LoyaltyScan: React.FC = () => {
    const router = useRouter();
    const campaignId = router.query.campaignId as string;
    const {
        onClickTapToClaim,
        onEligibileScreenView,
        onAppDownloadClick,
        onExploreDineoutImpression,
        onExploreDineoutClick,
        onClickLogin,
        onLoginAndNonAppUserScreenView,
    } = useDineoutAnalytics();

    const { headers } = useSwiggyHeaders();
    const { userLoggedIn, isLoading } = useFetchUserLoggedIn();
    const [isEligible, setIsEligible] = useState<"eligible" | "ineligible" | undefined>(undefined);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [error, setError] = useState(false);

    const [ineligiblePayload, setIneligiblePayload] = useState<IIneligiblePayload>({
        text: "",
        subText: "",
        bannerMessage: {
            text: "",
            subText: "",
        },
    });
    const [eligiblePayload, setEligiblePayload] = useState<IEligiblePayload>({
        text: "",
        subText: "",
        cashPillAmount: "",
        rewardsExpiryDays: "",
    });
    const handleLogin = async () => {
        onClickLogin({
            clickLogin: "login-click",
        });
        await handleLoginFn();
    };

    const handleCrossClick = () => {
        closeWebview();
    };

    const handleRetry = useCallback(() => {
        if (typeof window != "undefined") {
            window.location.reload();
        }
    }, []);

    const handleExploreSwiggyDineout = useCallback(() => {
        const sdk = getBridgeSdk();
        if (isEligible === LOYALTY_STATUS.INELIGIBLE) {
            onExploreDineoutClick({
                text: ineligiblePayload.text,
                subText: ineligiblePayload.subText,
            });
        } else {
            onExploreDineoutClick({
                text: LOYALTY_STATUS.ELIGIBLE,
            });
        }

        if (sdk?.navigateToScreen) {
            sdk.navigateToScreen({
                type: NavigationTypes.DEEPLINK,
                link: DINEOUT_HOME_DEEPLINK,
                params: {
                    shouldCloseWebview: true,
                    shouldRefresh: true,
                },
            }).catch(() => {
                // Silently handle navigation failure
            });
        }
    }, [isEligible, ineligiblePayload.text, ineligiblePayload.subText, onExploreDineoutClick]);

    const fetchLoyaltyScanData = useCallback(async () => {
        try {
            // Only proceed if we have a valid campaignId
            if (!router.isReady || !campaignId) {
                return;
            }
            setIsDataLoading(true);
            const response = await getLoyaltyScanData(campaignId);
            setIneligiblePayload({
                text: response.data.Text || "",
                subText: response.data.subText || "",
                bannerMessage: {
                    text: response.data.bannerMessage?.Text || "",
                    subText: response.data.bannerMessage?.subText || "",
                },
            });
            onEligibileScreenView({
                cashbackEarned: response.data.Text || "0",
                expiryInDays: response.data.rewardExpiryDays || "0",
                campaignId: Number(campaignId),
                statusCode: response.data.statusCode,
            });
            setIsEligible(checkIsEligible(response.data.statusCode));
            setEligiblePayload({
                text: response.data.Text || "",
                subText: response.data.subText || "",
                cashPillAmount: response.data.cashPillAmount || "",
                rewardsExpiryDays: response.data.rewardExpiryDays || "",
            });
        } catch {
            setError(true);
        } finally {
            setIsDataLoading(false);
        }
    }, [campaignId, onEligibileScreenView, router.isReady]);

    useEffect(() => {
        if (userLoggedIn) {
            fetchLoyaltyScanData();
        }
    }, [fetchLoyaltyScanData, userLoggedIn]);

    const handleDineCashAvail = useCallback(async () => {
        try {
            const response = await availDineCash(campaignId);
            onClickTapToClaim({
                cashbackEarned: eligiblePayload.text || "0",
                expiryInDays: eligiblePayload.rewardsExpiryDays || "0",
                campaignId: Number(campaignId),
            });
            return response;
        } catch {
            // Silently handle error
            setError(true);
        }
    }, [campaignId, eligiblePayload.rewardsExpiryDays, eligiblePayload.text, onClickTapToClaim]);

    if (error) {
        return (
            <>
                <CrossIcon onClick={handleCrossClick} isError={error} />
                <ErrorWrapper ctaHandler={handleRetry} ctaText={TRY_AGAIN} />
            </>
        );
    }

    if (isLoading || isDataLoading) {
        return (
            <PageWrapper>
                <FullPageLoader />
            </PageWrapper>
        );
    }

    if (!headers) {
        return (
            <PageWrapper>
                <Container>
                    <NonAppUser
                        onAppDownloadClick={onAppDownloadClick}
                        onRetry={handleRetry}
                        onLoginAndNonAppUserScreenView={onLoginAndNonAppUserScreenView}
                        campaignId={Number(campaignId)}
                    />
                </Container>
            </PageWrapper>
        );
    }

    if (!userLoggedIn) {
        return (
            <PageWrapper>
                <Container>
                    <CrossIcon onClick={handleCrossClick} />
                    <LoginPage
                        handleLogin={handleLogin}
                        onLoginAndNonAppUserScreenView={onLoginAndNonAppUserScreenView}
                        campaignId={Number(campaignId)}
                    />
                </Container>
            </PageWrapper>
        );
    }

    if (isEligible === undefined) {
        return <FullPageLoader />;
    }

    if (isEligible === LOYALTY_STATUS.ELIGIBLE) {
        return (
            <PageWrapper>
                <Container>
                    <CrossIcon onClick={handleCrossClick} />
                    <LottiePlayer
                        onDineCashAvail={handleDineCashAvail}
                        eligiblePayload={eligiblePayload}
                        onExploreDineout={handleExploreSwiggyDineout}
                        onCrossClick={handleCrossClick}
                    />
                </Container>
            </PageWrapper>
        );
    }

    if (isEligible === LOYALTY_STATUS.INELIGIBLE) {
        return (
            <PageWrapper>
                <Ineligible
                    text={ineligiblePayload.text}
                    subText={ineligiblePayload.subText}
                    bannerMessage={ineligiblePayload.bannerMessage}
                    handleExploreSwiggyDineout={handleExploreSwiggyDineout}
                    onClick={handleCrossClick}
                    onExploreDineoutImpression={onExploreDineoutImpression}
                />
            </PageWrapper>
        );
    }

    return null;
};

export default memo(LoyaltyScan);
